/**
 * Painel do Assistente ao lado do trabalho.
 *
 * É um SPLIT, não um overlay: enquanto aberto, empurra o conteúdo (padding à
 * direita no body) em vez de cobrir. A diferença é o uso real — ler a
 * conversa do cliente e perguntar sobre ela ao mesmo tempo só funciona se as
 * duas coisas estiverem visíveis. Em telas estreitas vira tela cheia, porque
 * dividir 380px em 400px de largura não ajuda ninguém.
 *
 * Mora no App, fora do Router: trocar de página não pode matar um turno em
 * andamento (ver o comentário no App.tsx).
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { History, Maximize2, MessageSquarePlus, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/_core/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { P_ASSISTANT_USE } from "@shared/permissions";
import { AssistenteChat } from "./AssistenteChat";
import { useAssistente } from "./useAssistente";

const CHAVE_CONVERSA = "assistente:ultima-conversa";
const CHAVE_LARGURA = "assistente:largura";
const LARGURA_PADRAO = 400;
const LARGURA_MIN = 320;
const LARGURA_MAX = 720;
/** Quantas conversas cabem no menu do dock sem virar rolagem. */
const RECENTES_NO_DOCK = 6;

function lerNumero(chave: string, padrao: number | null): number | null {
  try {
    const v = Number(localStorage.getItem(chave));
    return Number.isFinite(v) && v > 0 ? v : padrao;
  } catch {
    return padrao;
  }
}

/** Rotas onde o dock não aparece: públicas, sem layout, ou o próprio chat. */
const SEM_DOCK = ["/assistente", "/login", "/landing", "/onboarding", "/privacidade", "/termos", "/lgpd", "/exclusao-de-dados", "/super-admin"];

/**
 * Decide se o dock existe. Mora no App (fora do Router) para sobreviver à
 * troca de página com um turno em andamento — ver o comentário no App.tsx.
 */
export function AssistenteDockGate() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [location] = useLocation();
  if (!user || !(user as { onboardingCompleted?: boolean }).onboardingCompleted) return null;
  if (!can(P_ASSISTANT_USE)) return null;
  if (SEM_DOCK.some((p) => location === p || location.startsWith(p + "/") || location.startsWith(p + "?"))) return null;
  return <AssistenteDock />;
}

export function AssistenteDock() {
  const [aberto, setAberto] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(() => lerNumero(CHAVE_CONVERSA, null));
  const [largura, setLargura] = useState(() => lerNumero(CHAVE_LARGURA, LARGURA_PADRAO) ?? LARGURA_PADRAO);
  const [redimensionando, setRedimensionando] = useState(false);
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();

  const fechar = useCallback(() => setAberto(false), []);
  const esquecer = useCallback(() => setConversationId(null), []);
  // O hook vive aqui, e não dentro do painel: fechar não pode matar um turno.
  const sessao = useAssistente({ conversationId, onConversaCriada: setConversationId, onNavegou: undefined, onConversaInvalida: esquecer });

  const conversas = trpc.assistente.conversas.useQuery(undefined, { enabled: aberto, staleTime: 10_000 });

  const abrirTelaCheia = useCallback(() => {
    setAberto(false);
    setLocation(conversationId ? `/assistente?c=${conversationId}` : "/assistente");
  }, [conversationId, setLocation]);

  useEffect(() => {
    try {
      if (conversationId) localStorage.setItem(CHAVE_CONVERSA, String(conversationId));
      else localStorage.removeItem(CHAVE_CONVERSA);
    } catch {
      /* armazenamento indisponível: só perde a lembrança */
    }
  }, [conversationId]);

  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_LARGURA, String(largura));
    } catch {
      /* idem */
    }
  }, [largura]);

  // Empurra o conteúdo em vez de cobrir. Em mobile não divide: ocupa a tela.
  useEffect(() => {
    if (!aberto || isMobile) {
      document.body.style.paddingRight = "";
      return;
    }
    document.body.style.paddingRight = `${largura}px`;
    return () => {
      document.body.style.paddingRight = "";
    };
  }, [aberto, largura, isMobile]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setAberto((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!redimensionando) return;
    const mover = (e: MouseEvent) => {
      const nova = window.innerWidth - e.clientX;
      if (nova >= LARGURA_MIN && nova <= LARGURA_MAX) setLargura(nova);
    };
    const soltar = () => setRedimensionando(false);
    document.addEventListener("mousemove", mover);
    document.addEventListener("mouseup", soltar);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", mover);
      document.removeEventListener("mouseup", soltar);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [redimensionando]);

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--brand-accent)] text-white shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring md:bottom-6 md:right-6"
        aria-label="Abrir o Assistente (Ctrl+J)"
        title="Assistente (Ctrl+J)"
      >
        <Sparkles className={sessao.ocupado ? "h-5 w-5 animate-pulse" : "h-5 w-5"} />
      </button>
    );
  }

  return (
    <aside
      role="complementary"
      aria-label="Assistente"
      className={cn(
        "fixed right-0 top-0 z-40 flex h-dvh flex-col border-l bg-background shadow-xl",
        isMobile ? "w-full" : "",
      )}
      style={isMobile ? undefined : { width: largura }}
    >
      {!isMobile && (
        <div
          onMouseDown={() => setRedimensionando(true)}
          className="absolute left-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/20"
          aria-hidden
        />
      )}
      <div className="flex h-12 shrink-0 items-center gap-1 border-b pl-4 pr-2">
        <Sparkles className="h-4 w-4 shrink-0 text-[color:var(--brand-accent)]" />
        <span className="truncate text-sm font-medium">Assistente</span>
        <div className="ml-auto flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" title="Conversas recentes">
                <History className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
                Conversas recentes
              </DropdownMenuLabel>
              {(conversas.data ?? []).slice(0, RECENTES_NO_DOCK).map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => setConversationId(c.id)} className={cn(c.id === conversationId && "font-medium")}>
                  <span className="truncate">{c.title}</span>
                </DropdownMenuItem>
              ))}
              {conversas.data && conversas.data.length === 0 && (
                <p className="px-2 py-3 text-xs text-muted-foreground">Nenhuma ainda.</p>
              )}
              {/* O dock é estreito: a lista inteira vira rolagem infinita ao
                  lado do trabalho. Aqui ficam as recentes; o resto está na
                  tela cheia, que tem espaço para buscar, renomear e excluir. */}
              {(conversas.data?.length ?? 0) > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled={sessao.ocupado} onClick={abrirTelaCheia}>
                    <Maximize2 className="mr-2 h-3.5 w-3.5" />
                    <span>
                      Ver todas
                      {(conversas.data?.length ?? 0) > RECENTES_NO_DOCK ? ` (${conversas.data!.length})` : ""}
                    </span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Nova conversa" onClick={() => setConversationId(null)}>
            <MessageSquarePlus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={sessao.ocupado ? "Aguarde a resposta terminar" : "Abrir em tela cheia"}
            disabled={sessao.ocupado}
            onClick={abrirTelaCheia}
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Fechar (Ctrl+J)" onClick={fechar}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <AssistenteChat sessao={sessao} compacto className="flex-1" />
    </aside>
  );
}
